import { z } from 'zod';
/**
 * The segments board's saved views.
 *
 * **A view is not a segment**, which on this board is worth saying twice: the
 * tabs sit above a list of segments, and pressing one must never be mistaken
 * for making an audience. A view says which segments you are looking at, in
 * what order, with which columns showing.
 *
 * It holds a search term rather than a rule tree, because that is the filtering
 * this board has. When it grows a filter of its own the view gains a field; it
 * does not become a different kind of thing.
 */
/**
 * How the board is ordered.
 *
 * Simpler than a contact sort, which also names a declared field's key. A
 * segment has no declared fields, so the column is the whole of it — the
 * domain's `SortableSegmentColumnId`, restated here because this is what
 * crosses the wire.
 *
 * **The sortable ones, not every column.** "Asks for" is rendered prose and
 * "Connected" is counted per row by the database; neither can be ordered by,
 * and a saved view naming one was a sort no query could honour. Nothing has
 * ever stored one, because the board offers no control for a column it marks
 * unsortable.
 */
const segmentSortSchema = z.object({
    column: z.enum(['name', 'created', 'changed']),
    direction: z.enum(['asc', 'desc']),
});
/**
 * What a segments filter may ask.
 *
 * A vocabulary about *segments*, restated here because this is what crosses the
 * wire. It is not the contacts rule vocabulary and must never become it: that
 * one asks about people and becomes SQL, this asks about segments and is
 * applied in the browser.
 */
const segmentFilterSchema = z.object({
    children: z
        .array(z.object({
        field: z.enum(['name', 'asks_about', 'rules', 'created', 'changed']),
        operator: z.enum(['is', 'is_not', 'contains', 'not_contains', 'at_least', 'at_most']),
        value: z.string().max(200),
    }))
        .max(20),
});
export const saveSegmentViewRequestSchema = z.object({
    name: z.string().trim().min(1, 'a view needs a name').max(120),
    /** What was typed in the search box. Empty is everything. */
    search: z.string().max(200).default(''),
    /** Absent is no filter, which is every segment the search left. */
    filter: segmentFilterSchema.optional(),
    /** Absent is the board's own order, which is most recently changed first. */
    sort: segmentSortSchema.optional(),
    /** Column ids this view hides. Empty is every column showing. */
    hidden: z.array(z.string().max(80)).max(20).default([]),
});
export const segmentViewSchema = z.object({
    viewId: z.string(),
    name: z.string(),
    search: z.string(),
    filter: segmentFilterSchema.nullable(),
    sort: segmentSortSchema.nullable(),
    hidden: z.array(z.string()),
});
export const segmentViewListSchema = z.object({ views: z.array(segmentViewSchema) });
/**
 * Saving answers one of three ways, and the form acts on each.
 *
 * `name-taken` is an answer rather than a failed request: two tabs called
 * "Active" is a strip nobody can use, and retyping a name you already used is
 * an ordinary mistake to be told about. `not-found` is kept separate from it
 * because they are different sentences — one is about a name and the other is
 * about a view somebody else deleted while this one was open.
 */
export const saveSegmentViewResponseSchema = z.discriminatedUnion('status', [
    z.object({ status: z.literal('saved'), viewId: z.string() }),
    z.object({ status: z.literal('name-taken') }),
    z.object({ status: z.literal('not-found') }),
]);
//# sourceMappingURL=segmentViews.js.map